import React, { useEffect, useState } from 'react';
import { IListingData } from '@models/listing';
import StaticLayout from '@components/layout/StaticLayout';
import { useLazyQuery } from '@apollo/client';
import { listQuery } from './queries';
import { mapToListingData } from './utils';
import { actions, filterSectionsContent, headers } from './content';
import EnhancedTable from '@components/_old/table/EnhancedTable';
import { IListingFilterWidgetSection } from '@components/widgets/listing-filter-widget';
import ListingFilterWidget from '@components/widgets/listing-filter-widget/ListingFilterWidget';
import { IAbstractRecord } from '@models';
import _ from 'lodash';
import { getFilter, setFilter } from '@utils/filter-utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { capitalizeFirstLetter } from '@utils/formatting-utils';
import PolicyNumberRangeDrawer from './policy-number-range-drawer/PolicyNumberRangeDrawer';
import { DEFAULT_ERROR_TEXT } from '@utils';
import Loader from '@components/loader/Loader';

const PolicyNumberRangesPage: React.FC = () => {
  const [booted, setBooted] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [policyNumberRangeDrawerOpen, setPolicyNumberRangeDrawerOpen] =
    useState<boolean>(false);
  const params = useParams();
  const rangeType = params.rangeType;

  const FILTER_SESSION_KEY = 'policyNumberRangesFilter_' + rangeType;

  const initialFilterValues =
    Object.keys(getFilter(FILTER_SESSION_KEY)).length > 0
      ? getFilter(FILTER_SESSION_KEY)
      : {
          status: [],
        };

  const [filterValues, setFilterValues] =
    useState<IAbstractRecord>(initialFilterValues);

  const [filterSections, setFilterSections] =
    useState<IListingFilterWidgetSection[]>();

  const [tableData, setTableData] = useState<IListingData<any>>({
    pagedItems: {},
    pageSize: 10,
    pageNumber: 0,
    totalCount: 0,
  });

  const [listPolicyNumberPage] = useLazyQuery(listQuery());

  // Add open and close function for action button
  actions[0].onClick = () => {
    setPolicyNumberRangeDrawerOpen(true);
  };

  const loadData = async (
    currentPage = 0,
    pageSize = tableData.pageSize,
    filterV = filterValues
  ) => {
    setLoading(true);
    try {
      const result = await listPolicyNumberPage({
        variables: {
          currentPage: currentPage + 1,
          currentPageSize: pageSize,
          rangeStatus:
            filterV?.status && filterV?.status?.length > 0
              ? filterV.status
              : [],
          rangeType: rangeType.toUpperCase(),
        },
        fetchPolicy: 'no-cache',
      });

      if (result.error) {
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
        return;
      }

      const data = result.data;
      const newTableData = mapToListingData(data);
      setTableData({
        ...newTableData,
        pageNumber: currentPage,
        pageSize: pageSize,
      });

      const savedFilters = getFilter(FILTER_SESSION_KEY) || filterV;
      setFilterValues(savedFilters);

      const newFilterSections = filterSectionsContent(data, savedFilters);
      setFilterSections(newFilterSections);

      setFilter(savedFilters, FILTER_SESSION_KEY);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      setLoading(false);
      setBooted(true);
    }
  };

  const initialize = async (reset = false) => {
    const savedFilters = getFilter(FILTER_SESSION_KEY);
    await loadData(0, reset ? 10 : tableData.pageSize, savedFilters);
  };

  useEffect(() => {
    setBooted(false);
    initialize(true);
  }, [params.rangeType]);

  const handlePageChange = (page: number) => {
    loadData(page, tableData.pageSize, filterValues);
  };

  const handleRowsPerPageChange = (numberOfRecordsPerPage: number) => {
    loadData(0, numberOfRecordsPerPage, filterValues);
  };

  const onFilterUpdate = async (v: Record<string, any>) => {
    const newFilters = _.cloneDeep(v);
    setFilter(newFilters, FILTER_SESSION_KEY);
    setFilterValues(newFilters);
    await loadData(0, tableData.pageSize, newFilters);
  };

  const renderMainChildren = () => (
    <div style={{ marginTop: '20px' }}>
      <EnhancedTable
        title={capitalizeFirstLetter(rangeType) + ' Policy Number Ranges'}
        name="count"
        orderByAscendingByDefault
        defaultOrderByColumn="name"
        inline={false}
        data={tableData}
        headers={headers}
        handlePageChange={handlePageChange}
        handleRowsPerPageChange={handleRowsPerPageChange}
        currentPage={tableData.pageNumber}
        hideToolbar
        usePagination
        disableSelection
        actions={actions}
        loader={loading}
      />
      {policyNumberRangeDrawerOpen && (
        <PolicyNumberRangeDrawer
          open={policyNumberRangeDrawerOpen}
          onClose={() => setPolicyNumberRangeDrawerOpen(false)}
          onSuccess={() => {
            handlePageChange(0);
            setPolicyNumberRangeDrawerOpen(false);
          }}
          extension={rangeType}
        />
      )}
    </div>
  );

  const renderFilter = () =>
    filterSections && (
      <ListingFilterWidget
        name={capitalizeFirstLetter(rangeType) + ' Filter'}
        filters={filterSections}
        disabled={loading}
        onApplyFilter={onFilterUpdate}
      />
    );

  if (!booted) {
    return <Loader />;
  }

  return (
    <StaticLayout
      name={'Policy Number Ranges'}
      leftChildren={renderFilter()}
      mainChildren={renderMainChildren()}
    />
  );
};

export default PolicyNumberRangesPage;
